import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import type { PermissionState } from "@capacitor/core";
import { t } from "@/i18n/ko";
import type { PushPermission, PushPort } from "./types";

/** Android 알림 채널 id(10/28 확정, 하나). 서버 apps/server/src/push/fcm.ts ANDROID_CHANNEL_ID·AndroidManifest default_notification_channel_id 와 같은 값 */
export const PUSH_CHANNEL_ID = "default";
const REGISTER_TIMEOUT_MS = 10_000;
/** iOS 가 FCM 변환 없이 돌려준 APNs 원본 토큰(64자 hex). AppDelegate 에 Firebase Messaging 이 없으면 이 값이 온다 — 서버(FCM)가 쓸 수 없어 보내지 않는다 */
const RAW_APNS_TOKEN = /^[0-9a-f]{64}$/i;

let channelReady: Promise<void> | null = null;
/** Android 알림 채널 "함락 알림"(중요도 높음). 토큰 등록 전에 만든다 — 중요도는 처음 만들 때 굳는다 */
const ensureChannel = () => {
  if (Capacitor.getPlatform() !== "android") return Promise.resolve();
  channelReady ??= PushNotifications.createChannel({ id: PUSH_CHANNEL_ID, name: t("push.channel"), importance: 4, visibility: 0 }).catch(() => {
    channelReady = null;
  });
  return channelReady;
};

const toPermission = (s: PermissionState): PushPermission => (s === "granted" ? "granted" : s === "denied" ? "denied" : "prompt");

/**
 * @capacitor/push-notifications. 권한·토큰 등록(그 전에 Android 채널 생성)·알림 탭 처리(AC0-260). 발송 규칙은 아직 없다.
 */
export const nativePush: PushPort = {
  async permission() {
    try {
      return toPermission((await PushNotifications.checkPermissions()).receive);
    } catch {
      return "denied";
    }
  },
  async requestPermission() {
    try {
      return toPermission((await PushNotifications.requestPermissions()).receive);
    } catch {
      return "denied";
    }
  },
  async register() {
    await ensureChannel();
    const handles: Array<Promise<{ remove: () => Promise<void> }>> = [];
    const token = await new Promise<string | null>((resolve) => {
      const timer = setTimeout(() => resolve(null), REGISTER_TIMEOUT_MS);
      const done = (v: string | null) => {
        clearTimeout(timer);
        resolve(v);
      };
      handles.push(PushNotifications.addListener("registration", (t) => done(t.value)));
      handles.push(PushNotifications.addListener("registrationError", () => done(null)));
      PushNotifications.register().catch(() => done(null));
    });
    for (const h of handles) void h.then((x) => x.remove()).catch(() => undefined);
    if (!token || (Capacitor.getPlatform() === "ios" && RAW_APNS_TOKEN.test(token))) return null;
    return token;
  },
  onOpened(cb) {
    let removed = false;
    const handle = PushNotifications.addListener("pushNotificationActionPerformed", (a) => {
      if (removed) return;
      const data = (a.notification.data ?? {}) as Record<string, unknown>;
      cb(Object.fromEntries(Object.entries(data).filter((e): e is [string, string] => typeof e[1] === "string")));
    });
    return () => {
      removed = true;
      void handle.then((h) => h.remove()).catch(() => undefined);
    };
  },
};
